import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import api from '../services/api';
import {
  ALL_PERMISSION_NAMES,
  getRoleBasedPermissions,
  PAGE_PERMISSIONS,
} from '../constants/permissions';

const PermissionsContext = createContext();

export const usePermissions = () => {
  const context = useContext(PermissionsContext);
  if (!context) {
    throw new Error('usePermissions must be used within a PermissionsProvider');
  }
  return context;
};

export const PermissionsProvider = ({ children }) => {
  const { user } = useAuth();
  const [userPermissions, setUserPermissions] = useState([]);
  const [loading, setLoading] = useState(true);

  const normalizeArray = (value) => {
    if (Array.isArray(value)) return value;
    if (Array.isArray(value?.items)) return value.items;
    if (Array.isArray(value?.records)) return value.records;
    if (Array.isArray(value?.users)) return value.users;
    return [];
  };

  const fetchUserPermissions = useCallback(async () => {
    if (!user) {
      setUserPermissions([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);

      // If user is admin, they have all permissions
      if (user.role === 'admin') {
        const fallbackNormalized = ALL_PERMISSION_NAMES.map((p) => ({ permission: p }));
        setUserPermissions(fallbackNormalized);
        setLoading(false);
        return;
      }

      // For non-admin users, get their specific permissions
      try {
        const userId = user.userId || user.id;
        const userPermsResponse = await api.get(`/permissions/user/${userId}`);
        const userPerms = normalizeArray(userPermsResponse);
        setUserPermissions(userPerms);
      } catch (error) {
        // If no custom permissions, check role-based defaults
        const roleBasedPermissions = getRoleBasedPermissions(user.role);
        
        // Get all permissions and filter by role
        try {
          const allPermsResponse = await api.get('/permissions');
          const allPermissions = normalizeArray(allPermsResponse);
          
          const rolePermissions = allPermissions.filter(p => 
            roleBasedPermissions.includes(p.permission)
          );
          
          setUserPermissions(rolePermissions);
        } catch (error) {
          // Fallback: create role-based permissions
          const roleBasedPermissions = getRoleBasedPermissions(user.role);
          const fallbackPerms = roleBasedPermissions.map(p => ({ permission: p }));
          setUserPermissions(fallbackPerms);
        }
      }
    } catch (error) {
      console.error('[PermissionsContext] Error in fetchUserPermissions:', error);
      setUserPermissions([]);
    } finally {
      setLoading(false);
    }
  }, [user]);

  const hasActionPermission = useCallback((permission) => {
    return userPermissions.some((p) => p.permission === permission);
  }, [userPermissions]);

  const hasPagePermission = useCallback((pageKey) => {
    const requiredActions = PAGE_PERMISSIONS[pageKey];
    if (!requiredActions || requiredActions.length === 0) {
      return false;
    }

    return requiredActions.some((actionPermission) =>
      hasActionPermission(actionPermission),
    );
  }, [hasActionPermission]);

  const hasPermission = (permissionOrPageKey) => {
    const isPageKey = Object.prototype.hasOwnProperty.call(
      PAGE_PERMISSIONS,
      permissionOrPageKey,
    );

    const result = isPageKey
      ? hasPagePermission(permissionOrPageKey)
      : hasActionPermission(permissionOrPageKey);

    return result;
  };

  const hasAnyPermission = (permissions) => {
    if (loading) return false;
    return permissions.some(permission => hasPermission(permission));
  };

  const hasAllPermissions = (permissions) => {
    if (loading) return false;
    return permissions.every(permission => hasPermission(permission));
  };

  useEffect(() => {
    fetchUserPermissions();
  }, [fetchUserPermissions]);

  const value = {
    userPermissions,
    loading,
    hasPagePermission,
    hasActionPermission,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    refetchPermissions: fetchUserPermissions
  };

  return (
    <PermissionsContext.Provider value={value}>
      {children}
    </PermissionsContext.Provider>
  );
};
